import { useState } from 'react';
import { AlertTriangle, Wallet } from 'lucide-react';
import {
  BILLING_PLAN_LABELS,
  BillingPlan,
  MerchantStatus,
  SettlementStatus,
} from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { cn, formatCOP, formatFecha } from '@/lib/cn';
import {
  useCambiarPlan,
  useCuentaDelComercio,
  useRegistrarPago,
  useRegistrarPagoComercio,
  type CobroDeComercio,
} from '@/lib/queries';
import { Boton, Campo, Entrada, Modal, Seleccion } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

/**
 * La cuenta corrida de un comercio, con el botón que la pone en cero.
 *
 * Arriba, lo que debe ahora mismo —lo acumulado desde su último corte más lo
 * que quedó pendiente de cortes anteriores— y el formulario para registrar el
 * pago o el abono. Registrar hace el corte de una: congela lo acumulado,
 * aplica la plata de lo más viejo a lo más nuevo, y si salda todo el contador
 * arranca en cero con el siguiente pedido.
 *
 * Se abre desde Cobros y desde la fila del comercio en Comercios: es la misma
 * cuenta vista desde dos puertas, nunca dos números distintos.
 */
export function CuentaComercio({
  comercio,
  onCerrar,
}: {
  comercio: CobroDeComercio | null;
  onCerrar: () => void;
}) {
  const cuenta = useCuentaDelComercio(comercio?.id ?? '');
  const cambiar = useCambiarPlan();
  const pagar = useRegistrarPago();
  const pagarCuenta = useRegistrarPagoComercio();
  const avisos = useAvisos();
  const puede = usePuede();
  const gestiona = puede('cobros', 'gestionar');

  const [plan, setPlan] = useState<BillingPlan>(BillingPlan.COMMISSION);
  const [cuota, setCuota] = useState('');
  const [cobrando, setCobrando] = useState<string | null>(null);
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState('Transferencia');
  const [referencia, setReferencia] = useState('');
  const [montoCuenta, setMontoCuenta] = useState('');
  const [metodoCuenta, setMetodoCuenta] = useState('Transferencia');
  const [referenciaCuenta, setReferenciaCuenta] = useState('');

  // Sin comercio elegido no hay nada que abrir: los hooks ya corrieron arriba.
  if (!comercio) return null;

  const acumulado = cuenta.data?.acumulado;
  const pendiente = cuenta.data?.pendiente ?? comercio.pendiente;
  const deuda = (acumulado?.amountDue ?? comercio.acumulado) + pendiente;
  const estados = cuenta.data?.estados ?? [];

  const registrarPagoDeLaCuenta = () => {
    pagarCuenta.mutate(
      {
        merchantId: comercio.id,
        amount: Math.round(Number(montoCuenta) * 100),
        method: metodoCuenta.trim(),
        reference: referenciaCuenta.trim() || undefined,
      },
      {
        onSuccess: (r) => {
          avisos.exito(
            r.alDia
              ? 'Pagado. La cuenta quedó en cero y arranca a sumar con el próximo pedido.'
              : `Abono registrado. Le falta ${formatCOP(r.saldoRestante)}.`,
          );
          setMontoCuenta('');
          setReferenciaCuenta('');
        },
        onError: (e) => avisos.error(getErrorMessage(e)),
      },
    );
  };

  return (
    <Modal abierto onCerrar={onCerrar} titulo={comercio.name} ancho="lg">
      <div className="space-y-6 p-5">
        {/* ── Lo que debe ahora ── */}
        <section className="rounded-kick bg-kick-light p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-bold text-kick-dark">Debe ahora</h3>
            <p className={cn('text-2xl font-bold', deuda > 0 ? 'text-kick-red' : 'text-emerald-700')}>
              {formatCOP(deuda)}
            </p>
          </div>

          {acumulado && (
            <dl className="mt-2 space-y-1 text-sm text-kick-gray">
              <div className="flex justify-between">
                <dt>
                  {acumulado.plan === BillingPlan.MONTHLY
                    ? `Mensualidad (${acumulado.feeMonths} mes${acumulado.feeMonths === 1 ? '' : 'es'})`
                    : `Comisión de ${acumulado.ordersCount} pedidos`}
                </dt>
                <dd className="text-kick-ink">
                  {formatCOP(
                    acumulado.plan === BillingPlan.MONTHLY
                      ? acumulado.monthlyFee
                      : acumulado.commissionAmount,
                  )}
                </dd>
              </div>
              {acumulado.deliveriesRequested > 0 && (
                <div className="flex justify-between">
                  <dt>{acumulado.deliveriesRequested} envíos que pidió</dt>
                  <dd className="text-kick-ink">{formatCOP(acumulado.deliveryCharges)}</dd>
                </div>
              )}
              {pendiente > 0 && (
                <div className="flex justify-between">
                  <dt>Pendiente de cortes anteriores</dt>
                  <dd className="font-semibold text-kick-red">{formatCOP(pendiente)}</dd>
                </div>
              )}
              <p className="pt-1 text-xs">
                {acumulado.desde
                  ? `Acumulando desde el último corte (${formatFecha(acumulado.desde)}).`
                  : 'Nunca se le ha hecho corte: acumula desde su primer pedido.'}
              </p>
            </dl>
          )}

          {gestiona && deuda > 0 && (
            <div className="mt-4 grid gap-2 border-t border-kick-gray/15 pt-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
              <Campo etiqueta="Monto recibido" ayuda="En pesos; menos que la deuda es un abono">
                <Entrada
                  value={montoCuenta}
                  onChange={(e) => setMontoCuenta(e.target.value)}
                  inputMode="numeric"
                  placeholder={String(Math.round(deuda / 100))}
                />
              </Campo>
              <Campo etiqueta="Cómo pagó">
                <Entrada value={metodoCuenta} onChange={(e) => setMetodoCuenta(e.target.value)} />
              </Campo>
              <Campo etiqueta="Referencia">
                <Entrada
                  value={referenciaCuenta}
                  onChange={(e) => setReferenciaCuenta(e.target.value)}
                  placeholder="N° de consignación"
                />
              </Campo>
              <Boton
                cargando={pagarCuenta.isPending}
                disabled={!Number(montoCuenta)}
                onClick={registrarPagoDeLaCuenta}
              >
                Registrar pago
              </Boton>
            </div>
          )}

          {deuda <= 0 && (
            <p className="mt-2 text-sm font-semibold text-emerald-700">
              Al día. Vuelve a sumar con el próximo pedido entregado.
            </p>
          )}
        </section>

        {/* ── Plan ── */}
        <section>
          <h3 className="font-bold text-kick-dark">Cómo se le cobra</h3>
          <p className="mt-0.5 text-sm text-kick-gray">
            Hoy: {BILLING_PLAN_LABELS[comercio.plan]}
            {comercio.plan === BillingPlan.COMMISSION
              ? ` del ${comercio.commissionRate}%`
              : comercio.monthlyFee
                ? ` de ${formatCOP(comercio.monthlyFee)}`
                : ''}
          </p>

          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Campo etiqueta="Plan">
              <Seleccion value={plan} onChange={(e) => setPlan(e.target.value as BillingPlan)}>
                {Object.values(BillingPlan).map((p) => (
                  <option key={p} value={p}>
                    {BILLING_PLAN_LABELS[p]}
                  </option>
                ))}
              </Seleccion>
            </Campo>

            <Campo etiqueta="Mensualidad" ayuda={plan === BillingPlan.MONTHLY ? 'En pesos' : 'No aplica'}>
              <Entrada
                value={cuota}
                onChange={(e) => setCuota(e.target.value)}
                inputMode="numeric"
                placeholder="150000"
                disabled={plan !== BillingPlan.MONTHLY}
              />
            </Campo>

            <Boton
              disabled={!gestiona}
              cargando={cambiar.isPending}
              onClick={() =>
                cambiar.mutate(
                  {
                    merchantId: comercio.id,
                    plan,
                    monthlyFee:
                      plan === BillingPlan.MONTHLY ? Math.round(Number(cuota) * 100) : undefined,
                  },
                  {
                    onSuccess: () => avisos.exito('Plan actualizado'),
                    onError: (e) => avisos.error(getErrorMessage(e)),
                  },
                )
              }
            >
              Guardar
            </Boton>
          </div>

          <p className="mt-2 text-xs text-kick-gray">
            El cambio rige desde ya: pasar a mensualidad perdona la comisión que lleve acumulada
            sin cortar.
          </p>
        </section>

        {/* ── Cortes anteriores ── */}
        <section className="border-t border-kick-gray/15 pt-5">
          <h3 className="font-bold text-kick-dark">Cortes anteriores</h3>

          {cuenta.isLoading && <p className="mt-2 text-sm text-kick-gray">Cargando…</p>}

          {!cuenta.isLoading && estados.length === 0 && (
            <p className="mt-2 text-sm text-kick-gray">Todavía no se le ha hecho ningún corte.</p>
          )}

          <div className="mt-3 space-y-2">
            {estados.map((e) => {
              const pagado = e.status === SettlementStatus.PAID;
              const falta = e.amountDue - e.paidAmount;

              return (
                <div key={e.id} className="rounded-kick bg-kick-light p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-kick-ink">
                        {e.periodStart} → {e.periodEnd}
                      </p>
                      <p className="text-xs text-kick-gray">
                        {formatCOP(e.amountDue)}
                        {e.paidAmount > 0 && ` · abonado ${formatCOP(e.paidAmount)}`}
                        {!!e.dueDate && !pagado && ` · vence ${e.dueDate}`}
                      </p>
                    </div>

                    {pagado ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-900">
                        Pagado
                      </span>
                    ) : gestiona ? (
                      <Boton
                        variante="secundario"
                        className="px-3 py-1.5 text-xs"
                        onClick={() => {
                          setCobrando(e.id);
                          setMonto(String(Math.round(falta / 100)));
                        }}
                      >
                        Anotar pago
                      </Boton>
                    ) : (
                      <span className="text-xs text-kick-gray">pendiente</span>
                    )}
                  </div>

                  {cobrando === e.id && (
                    <div className="mt-3 grid gap-2 border-t border-kick-gray/15 pt-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
                      <Campo etiqueta="Monto">
                        <Entrada
                          value={monto}
                          onChange={(ev) => setMonto(ev.target.value)}
                          inputMode="numeric"
                        />
                      </Campo>
                      <Campo etiqueta="Cómo pagó">
                        <Entrada value={metodo} onChange={(ev) => setMetodo(ev.target.value)} />
                      </Campo>
                      <Campo etiqueta="Referencia">
                        <Entrada
                          value={referencia}
                          onChange={(ev) => setReferencia(ev.target.value)}
                          placeholder="N° de consignación"
                        />
                      </Campo>
                      <Boton
                        cargando={pagar.isPending}
                        onClick={() =>
                          pagar.mutate(
                            {
                              settlementId: e.id,
                              amount: Math.round(Number(monto) * 100),
                              method: metodo.trim(),
                              reference: referencia.trim() || undefined,
                            },
                            {
                              onSuccess: (r) => {
                                avisos.exito(
                                  (r as { settled: boolean }).settled
                                    ? 'Pagado. Si estaba suspendido, ya volvió a estar activo.'
                                    : 'Abono registrado',
                                );
                                setCobrando(null);
                                setReferencia('');
                              },
                              onError: (err) => avisos.error(getErrorMessage(err)),
                            },
                          )
                        }
                      >
                        Anotar
                      </Boton>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {comercio.status === MerchantStatus.SUSPENDED && (
          <div className="flex items-start gap-3 rounded-kick border border-red-200 bg-red-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <p className="text-sm text-red-800">
              {comercio.suspensionReason ?? 'Suspendido.'} Al registrar el pago que salda la deuda,
              vuelve a estar activo solo.
            </p>
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-kick-gray">
          <Wallet className="h-3.5 w-3.5" />
          Los pagos se anotan a mano: no hay pasarela conectada.
        </div>
      </div>
    </Modal>
  );
}
